import { 
    type ProgramGuideItem, 
    PlayItem, 
    HomeContentType,
    ContentType, 
    type OnDemandContent, 
    ProgramGuide, 
    OnDemandData, 
    VodContentType,
    type Channel,
    ItemPaging,
    isFizzTV,
    type OnDemandItem,
    type ContentProperties,
    ContentItemProps
} from '@ca.vmedia.tv/domains';
import { MixedCardData, type CardData, ProgramCardData, OnDemandCardData, type ChannelLiveCardData } from "@/components/Cards/types";
import { AppConstants } from "@/common";
import { Services } from "@ca.vmedia.tv/services";
import { Logger, ImgUrlUtils } from '@/utils';
import { ShowAllContentType } from "@/components/Header/types/LinkItemsCollection";
import { Strings } from "@/locales/types/enums/strings";
import { useNuxtApp } from '#app';
import { useEmptyLine } from "@/composables/useEmptyLine";
import { useChannelsStore } from "@/stores/Channels";
import { useProgramInfo } from "@/composables/useProgramInfo/useProgramInfo";
import { useApplicationStore } from "@/stores/Application";
import type { IProgramInfo } from "@/composables/useProgramInfo/types";
import type { AxiosError } from 'axios';
import { useUserInfoStore } from "@/stores/UserInfo";
import type { ExposeInterface } from './types';

export const useHomePageStore = defineStore('HomePage', (): ExposeInterface => {
    const UserInfoStoreModule = useUserInfoStore();
    const {
        isClient,
    } = storeToRefs(UserInfoStoreModule);
    const ApplicationStore = useApplicationStore();
    const {
        getCurrentTime: currentTime,
    } = storeToRefs(ApplicationStore);

    const ChannelsStore = useChannelsStore();
    const {
        getAvailableChannels: allChannels,
    } = storeToRefs(ChannelsStore);

    const { 
        emptyOnDemandData,
        emptyProgramGuide
    } = useEmptyLine();

    const { $i18n, $services, $domains } = useNuxtApp();

    const _timerIDs: Map<string, NodeJS.Timeout | number> = new Map();
    const setTimerID = (functionName: string, timerId: number): void => {
        _timerIDs.set(functionName, timerId);
    };
    const createTimerInterval = (fetchMethod: () => Promise<void>): void => {
        if (_timerIDs.has(fetchMethod.name) === true) {
            return;
        }

        const timerId = window.setInterval(
            () => {
                fetchMethod().catch((error) => {
                    Logger.captureError(error as AxiosError | Error);
                });
            },
            AppConstants.HomeRenewInterval
        );
        setTimerID(fetchMethod.name, timerId);
    };
    const clearHomeTimers = (): void => {
        for (const [_, timerId] of _timerIDs) {
            clearInterval(timerId);
        }
        _timerIDs.clear();
    };

    const filteredTrendsCards = (items: ProgramGuideItem[]): ProgramGuideItem[] => {
        const accumulator = {cardLive: [], cardVCloud: [], cardOther: []};
        const reducer = (acc: {[key: string]: ProgramGuideItem[]}, item: ProgramGuideItem) => {
            const currentChannel = allChannels.value.find((channel: Channel) => channel.id === item?.recentAirTime?.channelId) ?? null;
            if (PlayItem.isLive(item)) {
                acc.cardLive.push(item);
            } else if (PlayItem.isVCloud(item, currentChannel)) {
                acc.cardVCloud.push(item);
            } else {
                acc.cardOther.push(item);
            }
            return acc;
        };
        const reducedCards = items.reduce(reducer, accumulator);
        return[...reducedCards.cardLive, ...reducedCards.cardVCloud, ...reducedCards.cardOther];
    };

    const _movies: Ref<ProgramGuide | null> = ref(null);
    const getMovies: ComputedRef<ProgramGuide | null> = computed(() => _movies.value);
    const fetchMovies = async (): Promise<void> => {
        try {
            _movies.value = await Services.Instance.home.getHomeContent(
                HomeContentType.Movies,
                AppConstants.HomeItemsInLine,
                1,
                allChannels.value
            );

            createTimerInterval(fetchMovies);
        } catch (error) {
            _movies.value = emptyProgramGuide();
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const _sports: Ref<ProgramGuide | null> = ref(null);
    const getSports: ComputedRef<ProgramGuide | null> = computed(() => _sports.value);
    const fetchSports = async (): Promise<void> => {
        try {
            _sports.value = await Services.Instance.home.getHomeContent(
                HomeContentType.Sports,
                AppConstants.HomeItemsInLine,
                1,
                allChannels.value
            );

            createTimerInterval(fetchSports);
        } catch (error) {
            _sports.value = emptyProgramGuide();
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const _watchAgain: Ref<OnDemandData | null> = ref(null);
    const getWatchAgain: ComputedRef<OnDemandData | null> = computed(() => _watchAgain.value);
    const fetchWatchAgain = async (): Promise<void> => {
        try {
            _watchAgain.value = await Services.Instance.vod.getVodContent(
                VodContentType.WatchAgain,
                AppConstants.HomeItemsInLine,
                1
            );

            createTimerInterval(fetchWatchAgain);
        } catch (error) {
            _watchAgain.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const _recentlyOnDemand: Ref<OnDemandData | null> = ref(null);
    const getRecentlyOnDemand: ComputedRef<OnDemandData | null> = computed(() => _recentlyOnDemand.value);
    const fetchRecentlyOnDemand = async (): Promise<void> => {
        try {
            _recentlyOnDemand.value = await Services.Instance.vod.getVodContent(
                VodContentType.RecentlyOnDemand,
                AppConstants.HomeItemsInLine,
                1
            );
            createTimerInterval(fetchRecentlyOnDemand);
        } catch (error) {
            _recentlyOnDemand.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const _olympics: Ref<ProgramGuide | null> = ref(null);
    const getOlympics: ComputedRef<ProgramGuide | null> = computed(() => _olympics.value);
    const fetchOlympics = async (): Promise<void> => {
        try {
            _olympics.value = await Services.Instance.home.getHomeContent(
                HomeContentType.Olympics,
                AppConstants.HomeItemsInLine,
                1,
                allChannels.value
            );

            createTimerInterval(fetchOlympics);
        } catch (error) {
            _olympics.value = emptyProgramGuide();
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const _kids: Ref<ProgramGuide | null> = ref(null);
    const getKids: ComputedRef<ProgramGuide | null> = computed(() => _kids.value);
    const fetchKids = async (): Promise<void> => {
        try {
            _kids.value = await Services.Instance.home.getHomeContent(
                HomeContentType.Kids,
                AppConstants.HomeItemsInLine,
                1,
                allChannels.value
            );

            createTimerInterval(fetchKids);
        } catch (error) {
            _kids.value = emptyProgramGuide();
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const _continueWatching: Ref<OnDemandData | null> = ref(null);
    const getContinueWatching: ComputedRef<OnDemandData | null> = computed(() => _continueWatching.value);
    const fetchContinueWatching = async (): Promise<void> => {
        try {
            _continueWatching.value = await Services.Instance.vod.getVodContent(
                VodContentType.ContinueWatching,
                AppConstants.HomeItemsInLine,
                1
            );

            createTimerInterval(fetchContinueWatching);
        } catch (error) {
            _continueWatching.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const _news: Ref<ProgramGuide | null> = ref(null);
    const getNews: ComputedRef<ProgramGuide | null> = computed(() => _news.value);
    const fetchNews = async (): Promise<void> => {
        try {
            _news.value = await Services.Instance.home.getHomeContent(
                HomeContentType.News,
                AppConstants.HomeItemsInLine,
                1,
                allChannels.value
            );

            createTimerInterval(fetchNews);
        } catch (error) {
            _news.value = emptyProgramGuide();
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const _recentChannels: Ref<Channel[] | null> = ref(null);
    const getRecentChannels: ComputedRef<Channel[] | null> = computed(() => _recentChannels.value);
    const fetchRecentChannels = async (): Promise<void> => {
        try {
            const channels = await Services.Instance.domains.channelsRepository.getRecentChannels();

            const channelIds = channels.channelsIds as number[];
            
            // Request programs for all channels.
            await Services.Instance.guide.getSegmentFromCache(channelIds, currentTime.value); 

            const recentChannels: {id: string}[] = channelIds.map(channelId => {
                return {id:`${channelId}`};
            });
            _recentChannels.value = recentChannels as unknown as Channel[];

            createTimerInterval(fetchRecentChannels);
        } catch (error) {
            _recentChannels.value = [];
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const _trends: Ref<ProgramGuide | null> = ref(null);
    const getTrends: ComputedRef<ProgramGuide | null> = computed(() => _trends.value);
    const fetchTrends = async (): Promise<void> => {
        try {
            const data = await Services.Instance.home.getHomeContent(
                HomeContentType.Trends,
                AppConstants.HomeItemsInLine,
                1,
                allChannels.value
            );

            if (!data.items) {
                throw new Error('No items in trends');
            }
            
            const items = filteredTrendsCards(data.items);
            const paging =  new ItemPaging(AppConstants.HomeItemsInLine, 1, data.items.length, 1);

            _trends.value = new ProgramGuide(items, null, paging);

            createTimerInterval(fetchTrends);
        } catch (error) {
            _trends.value = emptyProgramGuide();
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const _liveSeries: Ref<ProgramGuide | null> = ref(null);
    const getLiveSeries: ComputedRef<ProgramGuide | null> = computed(() => _liveSeries.value);
    const fetchLiveSeries = async (): Promise<void> => {
        try {
            _liveSeries.value = await Services.Instance.home.getHomeContent(
                HomeContentType.LiveSeries,
                AppConstants.HomeItemsInLine,
                1,
                allChannels.value
            );

            createTimerInterval(fetchOlympics);
        } catch (error) {
            _liveSeries.value = emptyProgramGuide();
            Logger.captureError(error as AxiosError);
        }
    };

    const _isLoading: Ref<boolean> = ref(false);
    const getIsLoadingShowAll: ComputedRef<boolean> = computed(() => _isLoading.value);

    let _typePage: string | null = null;
    const _showAllItems: Ref<ProgramGuide | OnDemandData | null> = ref(null);
    const getShowAll: ComputedRef<CardData[] | null> = computed(() => {
        if (_showAllItems.value === null ||  _showAllItems.value.items === null) {
            return null;
        }

        if([ShowAllContentType.Kids, ShowAllContentType.News, ShowAllContentType.Sports, ShowAllContentType.Movies, ShowAllContentType.LiveSeries].includes(_typePage as ShowAllContentType)){
            return (_showAllItems.value.items as ProgramGuideItem[]).map((item: ProgramGuideItem) => new ProgramCardData(item));
        };

        if([ShowAllContentType.WatchAgain, ShowAllContentType.RecentlyOnDemand, ShowAllContentType.ContinueWatching].includes(_typePage as ShowAllContentType)){
            return _showAllItems.value.items.map((item: OnDemandItem) => new OnDemandCardData(item));
        };

        if(_typePage === ShowAllContentType.Trends){
            return (_showAllItems.value.items as ProgramGuideItem[]).map((item: ProgramGuideItem) => new MixedCardData(item));
        }

        return [];
    });
    const clearShowAllHome = (): void => {
        _showAllItems.value = null;
        _typePage = null;
    };
    const setShowAllHome = (type: string): void => {
        try {
            if (_showAllItems.value !== null) {
                return;
            };

            _typePage = type;

            switch (type) {
                case ShowAllContentType.Kids:
                    _showAllItems.value = new ProgramGuide(getKids.value?.items, null, getKids.value?.paging);
                    break;

                case ShowAllContentType.News:
                    _showAllItems.value = new ProgramGuide(getNews.value?.items, null, getNews.value?.paging);
                    break;

                case ShowAllContentType.Sports:
                    _showAllItems.value = new ProgramGuide(getSports.value?.items, null, getSports.value?.paging);
                    break;

                case ShowAllContentType.Movies:
                    _showAllItems.value = new ProgramGuide(getMovies.value?.items, null, getMovies.value?.paging);
                    break;

                case ShowAllContentType.RecentlyOnDemand:
                    _showAllItems.value = new OnDemandData(getRecentlyOnDemand.value?.items, null, getRecentlyOnDemand.value?.paging);
                    break;

                case ShowAllContentType.ContinueWatching:
                    _showAllItems.value = new OnDemandData(getContinueWatching.value?.items, null, getContinueWatching.value?.paging);
                    break;

                case ShowAllContentType.WatchAgain:
                    _showAllItems.value = new OnDemandData(getWatchAgain.value?.items, null, getWatchAgain.value?.paging);
                    break;

                case ShowAllContentType.Trends:
                    _showAllItems.value = new ProgramGuide(getTrends.value?.items, null, getTrends.value?.paging);
                    break;

                case ShowAllContentType.LiveSeries:
                    _showAllItems.value = new ProgramGuide(getLiveSeries.value?.items, null, getLiveSeries.value?.paging);
                    break;
                    
                default:
                    break;
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const _loadShowAllVodNextPage = async (pageNumber: number, typePage: string): Promise<void> => {
        try {
            const data = await Services.Instance.vod.getVodContent(
                VodContentType[typePage as keyof typeof VodContentType],
                AppConstants.HomeItemsInLine,
                pageNumber,
            );

            if (_showAllItems.value && data && data.items !== null && data.items.length != 0) {
                const items = (_showAllItems.value.items as OnDemandItem[]).concat(data.items);
                _showAllItems.value = new OnDemandData(items, null, _showAllItems.value.paging);
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const _loadShowAllHomeNextPage = async (pageNumber: number, typePage: string): Promise<void> => {
        try {
            const data = await Services.Instance.home.getHomeContent(
                HomeContentType[typePage as keyof typeof HomeContentType],
                AppConstants.HomeItemsInLine,
                pageNumber,
                allChannels.value
            );
    
            if (_showAllItems.value && data && data.items !== null && data.items.length != 0) {
                const items = (_showAllItems.value.items as ProgramGuideItem[]).concat(data.items);
                _showAllItems.value = new ProgramGuide(items, null, _showAllItems.value.paging);
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const loadShowAllNextPage = async (): Promise<void> => {
        if( _isLoading.value === true){
            return;
        }

        const pageNumber = !_showAllItems.value?.items || !_showAllItems.value?.paging?.itemsOnPage
            ? -1
            : Math.ceil(_showAllItems.value.items.length / _showAllItems.value.paging.itemsOnPage) + 1;
        if (pageNumber < 0 || !_showAllItems.value?.paging?.totalPages || pageNumber > _showAllItems.value.paging?.totalPages) {
            return;
        }

        try {
            _isLoading.value = true;

            if([ShowAllContentType.WatchAgain, ShowAllContentType.RecentlyOnDemand, ShowAllContentType.ContinueWatching].includes(_typePage as ShowAllContentType)){
                await _loadShowAllVodNextPage(pageNumber, _typePage!);
            } else {
                await _loadShowAllHomeNextPage(pageNumber, _typePage!);
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        } finally {
            _isLoading.value = false;
        }
    };
    
    const _isLoadingEpisodes: Ref<boolean> = ref(false);
    const getIsLoadingEpisodes: ComputedRef<boolean> = computed(() => _isLoadingEpisodes.value);
    const _setIsLoadingEpisodes = (isLoading: boolean) => {
        _isLoadingEpisodes.value = isLoading;
    };

    const getContinueWatchingTitle = computed((): string => isFizzTV ? $i18n.t(Strings.HOME_FIZZ_CONTINUE_WATCHING).toString() : $i18n.t(Strings.HOME_CONTINUE_WATCHING).toString());
    const getMoviesTitle = computed((): string => isFizzTV ? $i18n.t(Strings.HOME_FIZZ_MOVIES).toString() : $i18n.t(Strings.HOME_MOVIES).toString());
    const getSportsTitle = computed((): string => isFizzTV ? $i18n.t(Strings.HOME_FIZZ_SPORTS).toString() : $i18n.t(Strings.HOME_SPORTS).toString());
    const getKidsTitle = computed((): string =>isFizzTV ? $i18n.t(Strings.HOME_FIZZ_KIDS).toString() : $i18n.t(Strings.HOME_KIDS).toString());
    const getNewsTitle = computed((): string =>isFizzTV ? $i18n.t(Strings.HOME_FIZZ_NEWS).toString() : $i18n.t(Strings.HOME_NEWS).toString());
    const getOlympicsTitle = computed((): string => $i18n.t(Strings.HOME_OLYMPICS).toString());
    const getWatchAgainTitle = computed((): string => $i18n.t(Strings.HOME_WATCH_AGAIN).toString());
    const getTrendsTitle = computed((): string => $i18n.t(Strings.HOME_TRENDS).toString());
    const getRecentlyOnDemandTitle = computed((): string => $i18n.t(Strings.HOME_RECENTLY_ON_DEMAND).toString());
    const getRecentChannelsTitle = computed((): string => $i18n.t(Strings.HOME_RECENT_CHANNELS).toString());
    const getLiveSeriesTitle = computed((): string => $i18n.t(Strings.HOME_LIVE_SERIES).toString());

    const _currentItem: Ref<PlayItem | null> = ref(null);

    const _episodesProperties: Ref<ContentProperties[]> = ref([]);
    const getEpisodesProperties: ComputedRef<ContentProperties[]> = computed(() => _episodesProperties.value);

    const _episodes: Ref<OnDemandContent | null> = ref(null);
    const getEpisodes: ComputedRef<OnDemandContent | null> = computed(() => _episodes.value);
    const fetchEpisodes = async (item: PlayItem): Promise<void> => {
        try {
            if (!item.id) {
                return;
            }

            _currentItem.value = item;
            _clearEpisodes();
            const isVOD = [ContentType.VOD, ContentType.SVOD].includes(item.contentType as ContentType);
            _episodes.value = isVOD
                ? await Services.Instance.vod.getSVODSeasonEpisodes(item.id, AppConstants.EpisodesInLine, 1)
                : await Services.Instance.vod.getCraveTVSeasonEpisodes(item.id, AppConstants.EpisodesInLine, 1);

            // Check if current item exists in episodes, if not - load more pages
            await _ensureCurrentItemInEpisodes();
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };
    
    const _ensureCurrentItemInEpisodes = async (): Promise<void> => {
        if (!_currentItem.value?.id || !_episodes.value?.items) {
            return;
        }

        const maxAttempts = _episodes.value.paging?.totalPages || 10; // Prevent infinite loop
        let attempts = 0;

        while (attempts < maxAttempts) {
            const currentItemExists = _episodes.value.items.some(
                (episode: OnDemandItem) => episode.id === _currentItem.value?.id
            );

            if (currentItemExists) {
                break;
            }

            // Check if there are more pages to load
            const itemsOnPage = _episodes.value.paging?.itemsOnPage || AppConstants.EpisodesInLine;
            const totalPages = _episodes.value.paging?.totalPages || 0;
            const loadedPages = Math.ceil(_episodes.value.items.length / itemsOnPage);
            const hasMorePages = loadedPages < totalPages;

            if (!hasMorePages) {
                break;
            }

            // Load next page
            await fetchNextPageOfEpisodes();
            attempts++;
        }

        if (attempts >= maxAttempts) {
            Logger.captureError(new Error(`Reached maximum attempts (${maxAttempts}) while searching for current episode. Episode ID: ${_currentItem.value?.id}`));
        }
    };
    
    const fetchNextPageOfEpisodes = async (): Promise<void> => {
        const pageNumber = !getEpisodes.value?.items || !getEpisodes.value?.paging?.itemsOnPage
            ? -1
            : Math.ceil(getEpisodes.value.items.length / getEpisodes.value.paging.itemsOnPage) + 1;
        if (pageNumber < 0 || !getEpisodes.value?.paging?.totalPages || pageNumber > getEpisodes.value.paging?.totalPages) {
            return;
        }

        try {
            if  (!_currentItem.value?.id) {
                return;
            }
            _setIsLoadingEpisodes(true);

            const isVOD = [ContentType.VOD, ContentType.SVOD].includes(_currentItem.value.contentType as ContentType);
            const data = isVOD
                ? await Services.Instance.vod.getSVODSeasonEpisodes(_currentItem.value.id, AppConstants.EpisodesInLine, pageNumber)
                : await Services.Instance.vod.getCraveTVSeasonEpisodes(_currentItem.value.id, AppConstants.EpisodesInLine, pageNumber);
            if (!_episodes.value?.items || !data.items) {
                return;
            }
            _episodes.value.items = [..._episodes.value.items, ...data.items];
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        } finally {
            _setIsLoadingEpisodes(false);
        }
    };
    const _clearEpisodes = (): void => {
        _episodes.value = null;
        _episodesProperties.value = [];
    };

    const getEnableProgramInfo: boolean = true;
    const _programInfo: Ref<IProgramInfo | null> = ref(null);
    const getProgramInfo: ComputedRef<IProgramInfo | null> = computed(() => _programInfo.value);
    const setProgramInfo = (value: IProgramInfo | null): void => {
        _programInfo.value = value;
    };
    const initProgramInfo = async (cardData: ChannelLiveCardData | ProgramCardData  | OnDemandCardData | null): Promise<void> => {
        if (getEnableProgramInfo !== true || cardData === null || _programInfo.value !== null) {
            return;
        }

        try {
            let srgImg: string | null = null;
            let currentProgram: ProgramGuideItem | OnDemandItem | null = null;
            let currentChannel: Channel | null = null;

            if ('channel' in cardData && allChannels.value.length > 0) {
                currentChannel = allChannels.value.find(channel => channel.id === Number(cardData.getId())) ?? null;
                if (!currentChannel?.id) {
                    throw new Error('Channel not found');
                }
                currentProgram = await $services.guide.getChannelProgramByTime(currentChannel.id, currentTime.value);
                if (!currentProgram) {
                    throw new Error('Program not found');
                }
                srgImg = currentProgram.image;
            }

            if ('program' in cardData && allChannels.value.length > 0) {
                currentChannel = allChannels.value.find(channel => channel.id === cardData.getPlayItem().recentAirTime?.channelId) ?? null;
                currentProgram = cardData.getPlayItem();
                srgImg = currentProgram.image;
            }

            if ('item' in cardData) {
                currentProgram = cardData.getPlayItem();
                srgImg = await $domains.imagesRepository.getOnDemandBackgroundImage(cardData.getPlayItem().images);
            }

            srgImg = ImgUrlUtils.src(srgImg, AppConstants.HeaderImageWidth);

            const programInfo = useProgramInfo(currentProgram, currentChannel, srgImg);

            _programInfo.value = programInfo;
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };
    

    const clearAllHomePage = (): void => {
        clearShowAllHome();
        _movies.value = null;
        _sports.value = null;
        _watchAgain.value = null;
        _recentlyOnDemand.value = null;
        _olympics.value = null;
        _kids.value = null;
        _continueWatching.value = null;
        _news.value = null;
        _recentChannels.value = null;
        _clearEpisodes();
        _trends.value = null;
        _currentItem.value = null;
        _programInfo.value = null;
        _liveSeries.value = null;
        clearHomeTimers();
    };

    watch(
        () => getEpisodes.value ? getEpisodes.value.items : null,
        (items) => {
            if(isClient.value && items) {
                const ids = items.slice(-AppConstants.EpisodesInLine).map((itemEpisode: OnDemandItem) => itemEpisode.id);
                Services.Instance.domains.mediaRepository.getItemProperties(
                    ids as number[], 
                    _currentItem.value?.contentType as ContentType, 
                    [ContentItemProps.WatchedPosition]
                ).then((streamProperties) => {
                    _episodesProperties.value = [..._episodesProperties.value, ...streamProperties];
                }).catch((error) => {
                    Logger.captureError(error as AxiosError | Error);
                });
            }
        }
    );

    return {
        getMovies,
        getEpisodes,
        getEpisodesProperties,
        getShowAll,
        setShowAllHome,
        loadShowAllNextPage,
        clearAllHomePage,
        fetchEpisodes,
        fetchMovies,
        fetchSports,
        fetchWatchAgain,
        getSports,
        getWatchAgain,
        getRecentlyOnDemand,
        fetchRecentlyOnDemand,
        getOlympics,
        fetchOlympics,
        getKids,
        fetchKids,
        getContinueWatching,
        fetchContinueWatching,
        getNews,
        fetchNews,
        getRecentChannels,
        fetchRecentChannels,
        getTrends,
        fetchTrends,
        clearShowAllHome,
        getContinueWatchingTitle,
        getMoviesTitle,
        getSportsTitle,
        getKidsTitle,
        getNewsTitle,
        getOlympicsTitle,
        getWatchAgainTitle,
        getTrendsTitle,
        getRecentlyOnDemandTitle,
        getRecentChannelsTitle,
        getIsLoadingShowAll,
        getIsLoadingEpisodes,
        fetchNextPageOfEpisodes,
        getEnableProgramInfo,
        initProgramInfo,
        getProgramInfo,
        setProgramInfo,
        getLiveSeriesTitle,
        getLiveSeries,
        fetchLiveSeries,
        clearHomeTimers
    };
});